import React, { useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { surface, tone } from '../timeclock/tcFormat';
import Button from '@mui/material/Button';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ATTACHMENT_ACCEPT, DEPARTMENTS, departmentLabel, hours, kindColor, kindLabel, niceDate, readAttachment, shortDate, teamPost } from './teamFormat';

/**
 * Shared pieces of the Verlofplanner screens: the guideline checks, the
 * deadlines, stat cards and pickers.
 */

const STATUS_ICON = { ok: '✓', warn: '!', bad: '✕' };
const statusColor = (status) => (status === 'ok' ? tone.ok : status === 'warn' ? tone.warn : tone.bad);

/** Title and explanation of one check result from the backend. */
export const checkText = (t, lang, c) => {
    const p = c.params || {};
    const days = (list) => (list || []).map((d) => niceDate(d, lang)).join(', ');
    switch (c.key) {
        case 'notice':
            return [t('team_chk_notice', 'At least 1 working day ahead'),
                t('team_chk_notice_d', { defaultValue: '{{n}} working day(s) before the start.', n: p.n })];
        case 'dev':
            return [t('team_chk_dev', 'Development: at least 1 of Bert, Mathias and Kristof'),
                c.status === 'ok'
                    ? t('team_chk_dev_ok', 'Someone of development is available every day.')
                    : t('team_chk_dev_bad', { defaultValue: 'Nobody of development available on {{days}}.', days: days(p.days) })];
        case 'support': {
            const list = Object.keys(p.days || {}).map((d) => niceDate(d, lang) + ': ' + p.days[d]).join(' · ');
            return [t('team_chk_support', { defaultValue: 'Support: at most {{max}} away at the same time', max: p.max }),
                c.status === 'ok'
                    ? t('team_chk_support_ok', 'Fine on every day.')
                    : t('team_chk_support_bad', { defaultValue: 'Already away: {{list}}', list })];
        }
        case 'block':
            return [t('team_chk_block', 'At most 3 weeks in a row'),
                t('team_chk_block_d', { defaultValue: '{{from}} – {{to}}: {{n}} working days', from: niceDate(p.from, lang), to: niceDate(p.to, lang), n: p.workdays })
                + (p.joined ? ', ' + t('team_chk_block_joined', 'together with the leave next to it') : '')
                + '; ' + t('team_chk_block_bridge', 'weekends and holidays in between do not split it.')];
        case 'groot':
            return [t('team_chk_groot', 'Long leave (2 weeks or more)'),
                p.coordinated
                    ? t('team_chk_groot_coordinated', 'Coordinated with the department.')
                    : c.status === 'ok'
                        ? t('team_chk_groot_ok', 'Can be approved now.')
                        : t('team_chk_groot_wait', { defaultValue: 'Approved from {{date}}, unless coordinated with the department.', date: shortDate(p.from) })];
        case 'recup':
            return [t('team_chk_recup', 'Recup not below zero'),
                t('team_chk_recup_d2', { defaultValue: 'Balance on {{on}}: {{after}} · on 31/12: {{end}}.', on: niceDate(p.on, lang), after: hours(p.after, lang), end: hours(p.year_end, lang) })];
        case 'recup_late':
            return [t('team_chk_recup', 'Recup not below zero'),
                c.status === 'ok'
                    ? t('team_chk_recup_late_ok', { defaultValue: 'November and December: below zero on that day is allowed ({{after}}); on 31/12 {{end}} is left.', after: hours(p.after, lang), end: hours(p.year_end, lang) })
                    : t('team_chk_recup_late_bad', { defaultValue: 'On 31/12 you would be {{end}} short: you ask more recup than you earn this year. Nothing carries over.', end: hours(p.year_end, lang) })];
        case 'leave':
            return [t('team_chk_leave', 'Enough leave left'),
                t('team_chk_leave_d', { defaultValue: 'Left after this request: {{after}}.', after: hours(p.after, lang) })];
        case 'leave_no_right':
            return [t('team_chk_leave_no_right', { defaultValue: 'Leave right {{year}}', year: p.year }),
                t('team_chk_leave_no_right_d', 'Not entered yet; management checks it.')];
        case 'replacement':
            return [t('team_chk_replacement', 'Replacement day available'),
                t('team_chk_replacement_d', { defaultValue: '{{after}} left after this request (of {{right}}).', after: p.after, right: p.right })];
        default:
            return [c.key, ''];
    }
};

export const CheckList = ({ checks, t, lang }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(checks || []).map((c, i) => {
            const [title, detail] = checkText(t, lang, c);
            return (
                <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, mt: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800, color: '#fff', backgroundColor: statusColor(c.status) }}>{STATUS_ICON[c.status] || '?'}</Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>{title}</Typography>
                        {detail && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.4 }}>{detail}</Typography>}
                    </Box>
                </Box>
            );
        })}
    </Box>
);

/** Deadlines of the guidelines for one person. */
export const deadlineText = (t, lang, d) => {
    const p = d.params || {};
    switch (d.key) {
        case 'hoofdverlof':
            return d.status === 'ok'
                ? t('team_dl_main_ok', { defaultValue: 'Main leave (block of 2 weeks or more): {{from}} – {{to}}, {{n}} working days.', from: niceDate(p.block.from, lang), to: niceDate(p.block.to, lang), n: p.block.workdays })
                : t('team_dl_main', { defaultValue: 'Main leave: no block of {{min}} working days yet{{longest}}. Management looks at this per person.', min: p.min,
                    longest: p.block ? ' (' + t('team_dl_longest', 'longest') + ' ' + niceDate(p.block.from, lang) + ' – ' + niceDate(p.block.to, lang) + ', ' + p.block.workdays + ')' : '' });
        case 'unplanned':
            return p.days === null
                ? t('team_dl_unplanned_noright', 'At most 5 days unplanned: leave right not entered yet.')
                : t('team_dl_unplanned', { defaultValue: 'At most {{max}} days unplanned: now {{days}} days.', max: p.max, days: String(p.days).replace('.', lang === 'en' ? '.' : ',') });
        case 'all_planned':
            return d.status === 'ok'
                ? t('team_dl_planned_ok', 'Everything is planned.')
                : t('team_dl_planned', { defaultValue: 'Everything planned: still {{leave}} leave and {{recup}} recup without a date.', leave: hours(p.leave_h, lang), recup: hours(p.recup_h, lang) });
        case 'all_taken':
            return t('team_dl_taken', 'Everything taken; nothing carries over to next year.');
        default:
            return d.key;
    }
};

export const Deadlines = ({ list, t, lang }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {(list || []).map((d) => (
            <Box key={d.key} sx={{ display: 'grid', gridTemplateColumns: '20px 64px 1fr', gap: 1.25, alignItems: 'start', fontSize: 13.5 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 800, color: '#fff', backgroundColor: statusColor(d.status) }}>{STATUS_ICON[d.status]}</Box>
                <Box sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: '20px' }}>{shortDate(d.date)}</Box>
                <Box sx={{ lineHeight: 1.45 }}>{deadlineText(t, lang, d)}</Box>
            </Box>
        ))}
    </Box>
);

export const KindChip = ({ kind, t, theme, label }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: kindColor(kind, theme), flexShrink: 0 }} />
        {label || kindLabel(t, kind)}
    </Box>
);

export const StatusText = ({ status, t }) => {
    const map = {
        PENDING: [t('team_status_PENDING', 'Waiting'), tone.info],
        APPROVED: [t('team_status_APPROVED', 'Approved'), tone.ok],
        REJECTED: [t('team_status_REJECTED', 'Rejected'), tone.bad],
        CANCELLED: [t('team_status_CANCELLED', 'Cancelled'), tone.muted],
    };
    const [label, color] = map[status] || [status, tone.muted];
    return <Box component="span" sx={{ color, fontWeight: 600, fontSize: 12.5 }}>{label}</Box>;
};

export const StatCard = ({ theme, title, main, mainColor, lines, bar }) => {
    const s = surface(theme);
    return (
        <Box sx={{ ...s.card, p: 2.25, flex: '1 1 220px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: s.faint, mb: 0.75 }}>{title}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.15, color: mainColor, fontVariantNumeric: 'tabular-nums' }}>{main}</Typography>
            {bar !== undefined && (
                <Box sx={{ height: 6, borderRadius: 3, mt: 1, backgroundColor: s.hover, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: Math.max(0, Math.min(100, bar)) + '%', backgroundColor: kindColor('LEAVE', theme) }} />
                </Box>
            )}
            <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.4, columnGap: 2, fontSize: 13.5 }}>
                {lines.map(([label, value]) => (
                    <React.Fragment key={label}>
                        <Box sx={{ color: 'text.secondary' }}>{label}</Box>
                        <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

/** Colleague picker, grouped by department. */
export const StaffSelect = ({ staff, value, onChange, t, label, filter, sx, allowEmpty }) => (
    <TextField select size="small" label={label || t('team_col_name', 'Colleague')} value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 200, ...sx }}>
        {allowEmpty && <MenuItem value="">—</MenuItem>}
        {DEPARTMENTS.flatMap((dept) => {
            const people = staff.filter((p) => p.department === dept && (!filter || filter(p)));
            if (!people.length) return [];
            return [
                <MenuItem key={'h' + dept} disabled sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: '0.7 !important' }}>
                    {departmentLabel(t, dept)}
                </MenuItem>,
                ...people.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>),
            ];
        })}
    </TextField>
);

export const Panel = ({ theme, title, children, sx }) => {
    const s = surface(theme);
    return (
        <Box sx={{ ...s.card, p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.5, ...sx }}>
            {title && <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>}
            {children}
        </Box>
    );
};

/** File picker for an attachment that is mailed along (sickness, special leave). */
export const AttachmentPicker = ({ t, value, onChange, onError, note, compact }) => {
    const input = React.useRef(null);
    const pick = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        readAttachment(file).then(onChange).catch((err) => {
            onChange(null);
            if (onError) onError(err.message === 'size' ? t('team_err_attachment_size', 'Attachment: at most 5 MB.') : t('errorOccured', 'Something went wrong'));
        });
    };
    return (
        <Box>
            <input ref={input} type="file" accept={ATTACHMENT_ACCEPT} style={{ display: 'none' }} onChange={pick} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} onClick={() => input.current?.click()} sx={{ textTransform: 'none' }}>
                    {value ? value.name : t('team_attach_pick', 'Add an attachment')}
                </Button>
                {value && <Button size="small" onClick={() => onChange(null)} sx={{ textTransform: 'none' }}>{t('team_attach_remove', 'Remove')}</Button>}
            </Box>
            {note && !compact && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>{note}</Typography>}
        </Box>
    );
};

/**
 * SoftTouch Vlaanderen client for an installation or intervention (searches lic_clients, management only).
 * value: {id, name, city?, klnr?} or null.
 */
export const ClientField = ({ t, token, value, onChange, autoFocus, sx }) => {
    const [options, setOptions] = useState(value ? [value] : []);
    const [input, setInput] = useState('');
    const seq = useRef(0);

    useEffect(() => {
        if (input.trim().length < 2) { setOptions(value ? [value] : []); return undefined; }
        const n = ++seq.current;
        const timer = setTimeout(() => {
            teamPost('clients', token, { q: input }).then((response) => {
                if (n !== seq.current) return;
                const found = response?.success ? (response.data || []) : [];
                // The chosen client stays an option, otherwise the field loses it.
                setOptions(value && !found.some((o) => o.id === value.id) ? [value, ...found] : found);
            });
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input, token]);

    return (
        <Autocomplete size="small" options={options} value={value} filterOptions={(x) => x} sx={sx}
            getOptionLabel={(o) => o.name + (o.city ? ' · ' + o.city : '') + (o.klnr ? ' (' + o.klnr + ')' : '')}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(e, v) => onChange(v)}
            // 'reset' = MUI filling in the label of the chosen client: no new search for that.
            onInputChange={(e, v, reason) => { if (reason !== 'reset') setInput(v); }}
            noOptionsText={input.trim().length < 2 ? t('team_client_type', 'Type at least 2 letters') : t('team_client_none', 'No client found')}
            renderInput={(params) => <TextField {...params} autoFocus={autoFocus} label={t('team_client', 'Client (SoftTouch Vlaanderen)')} />} />
    );
};
